import {
  Alert,
  Box,
  Button,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import {
  AssignmentTurnedInOutlined as ConsentIcon,
  HubOutlined as HubIcon,
  LinkOutlined as LinkIcon,
  VerifiedUserOutlined as ShieldIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import { useState, type FormEvent, type ReactNode } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '@/auth/AuthContext';
import { decodeJwt, isExpired, isPlatformAdmin } from '@/auth/jwt';
import { tokenStore } from '@/auth/tokenStore';
import { auth } from '@/api/auth';

/**
 * Split-screen sign-in page. Design ported from v1 (gj-clinic shape):
 *   - Left (52% wide, hidden < md): brand-gradient panel with subtle dot
 *     grid + soft glow blobs, logo, status badge, large headline with
 *     accent run-on, and three milestone feature cards (M1/M2/M3).
 *   - Right: form panel — "Welcome back" with an accent underline,
 *     email + password by default, dev-only token-paste fallback via
 *     a link at the bottom.
 *
 * Two motion touches: the form panel fades-up on mount; feature cards
 * stagger in from the left. Everything else stays static so the page
 * feels solid rather than busy.
 */

const FEATURES: { icon: ReactNode; title: string; body: string }[] = [
  { icon: <ShieldIcon  sx={{ fontSize: 20 }} />, title: 'M1 — Patient Verify',     body: 'ABHA address & Aadhaar OTP onboarding with revisit detection.' },
  { icon: <LinkIcon    sx={{ fontSize: 20 }} />, title: 'M2 — Care Context Link',  body: 'Discover and link patient records across HIPs.' },
  { icon: <ConsentIcon sx={{ fontSize: 20 }} />, title: 'M3 — Consent & Data Push', body: 'Consent artefact handling, FHIR R4 bundle generation and push.' },
];

export function LoginPage() {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, portal, signIn } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (isAuthenticated && portal) {
    return <Navigate to={portal === 'platform' ? '/platform' : '/tenant'} replace />;
  }

  const finishSignIn = (accessToken: string) => {
    const claims = decodeJwt(accessToken);
    if (!claims) {
      setError("Server returned a token I couldn't decode.");
      return;
    }
    if (isExpired(claims)) {
      setError('Server returned an already-expired token.');
      return;
    }
    signIn(accessToken);
    const fromState = location.state as { from?: string } | null;
    const fallback = isPlatformAdmin(claims) ? '/platform' : '/tenant';
    navigate(fromState?.from ?? fallback, { replace: true });
  };

  const onSubmitCredentials = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    if (!email || !password) {
      setError('Email and password are required.');
      return;
    }
    setSubmitting(true);
    try {
      const pair = await auth.login({ email: email.trim(), password });
      tokenStore.setRefresh(pair.refreshToken);
      finishSignIn(pair.accessToken);
    } catch (e) {
      const detail = (e as { response?: { data?: { error?: string; message?: string } } })?.response?.data;
      setError(messageFor(detail?.error) ?? detail?.message ?? (e as Error).message ?? 'Login failed.');
    } finally {
      setSubmitting(false);
    }
  };

  // Token-paste fallback was removed — this is a credentials-only login.

  const brandDeep   = theme.palette.brand.dark;
  const brandMain   = theme.palette.brand.main;
  const brandLight  = theme.palette.brand.lighter;
  const accentMain  = theme.palette.accent.main;
  const accentDark  = theme.palette.accent.dark;
  const accentLight = theme.palette.accent.lighter;

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: theme.palette.surface.canvas }}>
      {/* ─── Left brand panel ─────────────────────────────────────── */}
      <Box
        sx={{
          flex: '0 0 52%',
          position: 'relative',
          overflow: 'hidden',
          background: `linear-gradient(155deg, ${brandDeep} 0%, ${brandMain} 55%, ${brandLight} 100%)`,
          display: { xs: 'none', md: 'flex' },
          flexDirection: 'column',
          justifyContent: 'space-between',
          color: '#fff',
          p: { md: 6, lg: 8 },
        }}
      >
        {/* Dot grid */}
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            inset: 0,
            opacity: 0.06,
            backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
            backgroundSize: '32px 32px',
            pointerEvents: 'none',
          }}
        />
        {/* Glow blobs */}
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            top: '-10%',
            right: '-15%',
            width: 520,
            height: 520,
            borderRadius: '50%',
            background: `radial-gradient(circle, ${accentMain}55, transparent 65%)`,
            filter: 'blur(80px)',
            pointerEvents: 'none',
          }}
        />
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            bottom: '-20%',
            left: '-15%',
            width: 480,
            height: 480,
            borderRadius: '50%',
            background: `radial-gradient(circle, ${accentDark}66, transparent 70%)`,
            filter: 'blur(90px)',
            pointerEvents: 'none',
          }}
        />

        {/* Logo */}
        <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: 2,
              bgcolor: 'rgba(255,255,255,0.08)',
              border: '1px solid rgba(255,255,255,0.12)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backdropFilter: 'blur(8px)',
            }}
          >
            <HubIcon sx={{ color: '#fff', fontSize: 24 }} />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 800, fontSize: 18, letterSpacing: '-0.01em' }}>
              GJ Global
            </Typography>
            <Typography sx={{ fontSize: 12, color: 'rgba(255,255,255,0.7)', letterSpacing: 0.4, textTransform: 'uppercase' }}>
              ABDM Connector
            </Typography>
          </Box>
        </Box>

        {/* Marketing copy + features */}
        <Box sx={{ position: 'relative', zIndex: 1, maxWidth: 520 }}>
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1,
              px: 1.5,
              py: 0.5,
              borderRadius: 50,
              border: '1px solid rgba(255,255,255,0.18)',
              bgcolor: 'rgba(255,255,255,0.06)',
              mb: 3,
            }}
          >
            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: accentMain }} />
            <Typography sx={{ fontSize: 11, fontWeight: 600, letterSpacing: 0.4, color: 'rgba(255,255,255,0.85)' }}>
              ABDM CERTIFIED · FHIR R4 · MULTI-TENANT
            </Typography>
          </Box>

          <Typography
            sx={{
              fontSize: { md: 38, lg: 44 },
              fontWeight: 800,
              lineHeight: 1.1,
              letterSpacing: '-0.025em',
              mb: 2,
            }}
          >
            Healthcare data,
            <Box component="span" sx={{ color: accentMain, display: 'block' }}>
              made interoperable.
            </Box>
          </Typography>
          <Typography sx={{ fontSize: 15, color: 'rgba(255,255,255,0.72)', lineHeight: 1.7, mb: 4, maxWidth: 460 }}>
            One connector for every ABDM milestone. Patient onboarding, care context
            linking and consent-based data exchange — all auditable, all yours.
          </Typography>

          <Stack spacing={1.75}>
            {FEATURES.map((f, i) => (
              <Box
                key={f.title}
                component={motion.div}
                initial={{ opacity: 0, x: -12 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.32, delay: 0.05 * i, ease: [0.2, 0, 0, 1] }}
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1.5,
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  transition: 'transform 0.18s ease, background-color 0.18s ease',
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,0.08)',
                    transform: 'translateX(2px)',
                  },
                }}
              >
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: 1.5,
                    bgcolor: 'rgba(255,255,255,0.08)',
                    color: accentLight,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  {f.icon}
                </Box>
                <Box>
                  <Typography sx={{ fontSize: 14, fontWeight: 700, mb: 0.25 }}>{f.title}</Typography>
                  <Typography sx={{ fontSize: 12.5, color: 'rgba(255,255,255,0.6)', lineHeight: 1.5 }}>
                    {f.body}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Stack>
        </Box>

        {/* Footer */}
        <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography sx={{ fontSize: 11, color: 'rgba(255,255,255,0.32)' }}>
            © {new Date().getFullYear()} GJ Global Soft · ABDM Connector v2.0
          </Typography>
          <Stack direction="row" spacing={2.5}>
            <Typography sx={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', cursor: 'pointer', '&:hover': { color: '#fff' } }}>Privacy</Typography>
            <Typography sx={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', cursor: 'pointer', '&:hover': { color: '#fff' } }}>Terms</Typography>
            <Typography sx={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', cursor: 'pointer', '&:hover': { color: '#fff' } }}>Support</Typography>
          </Stack>
        </Box>
      </Box>

      {/* ─── Right form panel ─────────────────────────────────────── */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          px: { xs: 3, sm: 5, md: 6 },
          py: 5,
          position: 'relative',
        }}
      >
        <Box
          component={motion.div}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.32, ease: [0.2, 0, 0, 1] }}
          sx={{ width: '100%', maxWidth: 420 }}
        >
          {/* Mobile-only header */}
          <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center', gap: 1.5, mb: 4 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                background: `linear-gradient(155deg, ${brandDeep}, ${brandMain})`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <HubIcon sx={{ color: '#fff', fontSize: 22 }} />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 800, fontSize: 16, color: brandDeep }}>
                GJ Global
              </Typography>
              <Typography sx={{ fontWeight: 600, fontSize: 11, color: brandMain, letterSpacing: 0.4, textTransform: 'uppercase' }}>
                ABDM Connector
              </Typography>
            </Box>
          </Box>

          <Typography
            sx={{
              fontSize: 30,
              fontWeight: 800,
              color: brandDeep,
              letterSpacing: '-0.02em',
              mb: 0.5,
              lineHeight: 1.15,
            }}
          >
            Welcome back
          </Typography>
          <Typography sx={{ fontSize: 14, color: 'text.secondary', mb: 1.5 }}>
            Sign in to continue to the ABDM Connector
          </Typography>
          <Box
            sx={{
              width: 56,
              height: 4,
              borderRadius: 2,
              bgcolor: accentMain,
              mb: 4,
              boxShadow: `0 0 12px ${accentMain}80`,
            }}
          />

          {/* Form */}
          <Box component="form" onSubmit={onSubmitCredentials}>
            <Stack spacing={2.5}>
              <TextField
                autoFocus
                label="Email or username"
                // The same /api/auth/login endpoint accepts both plain
                // usernames (e.g. "medzio") and email-style values
                // (e.g. "ops@ayushsetu.io") — the user_master.username
                // column is whatever was typed at create time. Using
                // type="email" here would block plain usernames at HTML
                // validation time, so use type="text" and let the server
                // decide if the credential is valid.
                type="text"
                autoComplete="username"
                inputMode="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={submitting}
                fullWidth
                helperText="Tenants sign in with the username from user_master (e.g. medzio); platform admins use their email."
              />
              <TextField
                label="Password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={submitting}
                fullWidth
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title={showPassword ? 'Hide password' : 'Show password'} arrow>
                        <IconButton
                          edge="end"
                          size="small"
                          aria-label={showPassword ? 'hide password' : 'show password'}
                          onClick={() => setShowPassword((v) => !v)}
                          // Stops focus jumping out of the field on click.
                          onMouseDown={(e) => e.preventDefault()}
                          tabIndex={-1}
                        >
                          {showPassword ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
              />
              {error && (
                <Alert severity="error" sx={{ borderRadius: theme.radius.sm + 'px' }}>
                  {error}
                </Alert>
              )}
              <Button
                type="submit"
                variant="contained"
                size="large"
                fullWidth
                disabled={submitting}
                sx={{
                  py: 1.25,
                  fontWeight: 600,
                  bgcolor: brandMain,
                  '&:hover': { bgcolor: brandDeep },
                }}
              >
                {submitting ? 'Signing in…' : 'Sign in'}
              </Button>
            </Stack>
          </Box>

          {/* Help footer (desktop only) */}
          <Typography
            sx={{
              display: { xs: 'none', md: 'block' },
              mt: 4,
              pt: 3,
              borderTop: `1px solid ${theme.palette.border.subtle}`,
              fontSize: 12,
              color: 'text.secondary',
              textAlign: 'center',
            }}
          >
            Having trouble signing in? Contact your tenant administrator.
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

function messageFor(reason: string | undefined): string | null {
  switch (reason) {
    case 'bad_credentials':   return "That email and password don't match an active account.";
    case 'user_disabled':     return 'This account is disabled. Contact your platform admin.';
    case 'refresh_invalid':   return 'Your session is invalid. Please sign in again.';
    case 'refresh_expired':   return 'Your session expired. Please sign in again.';
    case 'refresh_revoked':   return 'Your session was revoked. Please sign in again.';
    default:                  return null;
  }
}
